// Tiny ambient-sound helper using the Web Audio API.
// No assets, no external deps. All sounds are short, premium, non-annoying.

let ctx = null;
// Module-level throttle map so rapid hovers don't stack
const lastPlayed = {};

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

const blip = (audio, freq, start, dur, type = "sine", peak = 0.18) => {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, audio.currentTime + start);
  gain.gain.setValueAtTime(0.0001, audio.currentTime + start);
  gain.gain.exponentialRampToValueAtTime(peak, audio.currentTime + start + 0.01);
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    audio.currentTime + start + dur
  );
  osc.connect(gain).connect(audio.destination);
  osc.start(audio.currentTime + start);
  osc.stop(audio.currentTime + start + dur + 0.02);
};

// Frequency sweep (whoosh / transition)
const sweep = (audio, fromFreq, toFreq, start, dur, type = "sawtooth", peak = 0.12) => {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  const filter = audio.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1800;
  osc.type = type;
  osc.frequency.setValueAtTime(fromFreq, audio.currentTime + start);
  osc.frequency.exponentialRampToValueAtTime(
    toFreq,
    audio.currentTime + start + dur
  );
  gain.gain.setValueAtTime(0.0001, audio.currentTime + start);
  gain.gain.exponentialRampToValueAtTime(peak, audio.currentTime + start + 0.04);
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    audio.currentTime + start + dur
  );
  osc.connect(filter).connect(gain).connect(audio.destination);
  osc.start(audio.currentTime + start);
  osc.stop(audio.currentTime + start + dur + 0.05);
};

// Throttle helper — returns true if allowed to play
const throttle = (key, ms) => {
  const now = Date.now();
  if (lastPlayed[key] && now - lastPlayed[key] < ms) return false;
  lastPlayed[key] = now;
  return true;
};

// =========================================================================
// PUBLIC API
// =========================================================================

// Two-note ascending blip used when entering the site (after intro).
export const playEnterSound = () => {
  const audio = getCtx();
  if (!audio) return;
  blip(audio, 320, 0, 0.12, "triangle", 0.16);
  blip(audio, 540, 0.08, 0.18, "triangle", 0.14);
  blip(audio, 880, 0.18, 0.22, "sine", 0.08);
};

// Cinematic whoosh used when transitioning between major screens.
export const playPageTransition = () => {
  const audio = getCtx();
  if (!audio) return;
  sweep(audio, 180, 1400, 0, 0.6, "sawtooth", 0.1);
  sweep(audio, 1200, 240, 0.05, 0.55, "sine", 0.05);
  // little tail click
  blip(audio, 2400, 0.55, 0.06, "triangle", 0.05);
};

// Soft tick used per character while chaos resolves (rate-limited).
export const playTypeTick = () => {
  if (!throttle("type", 55)) return;
  const audio = getCtx();
  if (!audio) return;
  const freq = 1600 + Math.random() * 600;
  blip(audio, freq, 0, 0.025, "square", 0.025);
};

// Subtle micro-tick used on hover-ready CTAs.
export const playHoverTick = () => {
  if (!throttle("hover", 90)) return;
  const audio = getCtx();
  if (!audio) return;
  blip(audio, 1200, 0, 0.04, "square", 0.04);
};

// Premium hover swoosh — used only on primary CTAs (subscribe, watch, register).
export const playHoverSwoosh = () => {
  if (!throttle("swoosh", 220)) return;
  const audio = getCtx();
  if (!audio) return;
  sweep(audio, 700, 1600, 0, 0.18, "triangle", 0.06);
};
